import { Fragment, startTransition } from 'react';

import { TaskCriticalityEnum } from 'src/graphql/schema';
import {
  Select,
  SelectClose,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';

import { timeRangeOptions, criticalityOptions, eventTypeOptions, type ITimeRangeValue } from '../model';

type Props = {
  timeRange: ITimeRangeValue | null;
  eventTypes: string | null;
  criticality: TaskCriticalityEnum | null;
  onTimeRangeChange: (value: ITimeRangeValue | null) => void;
  onEventTypesChange: (value: string | null) => void;
  onCriticalityChange: (value: TaskCriticalityEnum | null) => void;
};

const TasksFilterBar = ({
  timeRange,
  eventTypes,
  criticality,
  onTimeRangeChange,
  onEventTypesChange,
  onCriticalityChange,
}: Props) => {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
      <div className="relative sm:w-50">
        <Select
          value={timeRange ?? ''}
          onValueChange={(v) => startTransition(() => onTimeRangeChange(v as ITimeRangeValue))}
        >
          <SelectTrigger withCloseButton={Boolean(timeRange)} className="bg-white w-full">
            <SelectValue placeholder="Filter by time range" />
          </SelectTrigger>
          <SelectContent>
            {timeRangeOptions.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SelectClose isVisible={Boolean(timeRange)} onClose={() => onTimeRangeChange(null)} />
      </div>

      <div className="relative sm:w-50">
        <Select value={eventTypes ?? ''} onValueChange={(v) => startTransition(() => onEventTypesChange(v))}>
          <SelectTrigger withCloseButton={Boolean(eventTypes)} className="bg-white w-full">
            <SelectValue placeholder="Filter by scope" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(eventTypeOptions).map(([key, { label, separator, options }]) => {
              return (
                <Fragment key={key}>
                  <SelectGroup>
                    <SelectLabel>{label}</SelectLabel>
                    {options.map(({ label, value }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectGroup>

                  {separator && <SelectSeparator />}
                </Fragment>
              );
            })}
          </SelectContent>
        </Select>
        <SelectClose isVisible={Boolean(eventTypes)} onClose={() => onEventTypesChange(null)} />
      </div>
      <div className="relative sm:w-50">
        <Select
          value={criticality ?? ''}
          onValueChange={(v) => startTransition(() => onCriticalityChange(v as TaskCriticalityEnum))}
        >
          <SelectTrigger withCloseButton={Boolean(criticality)} className="bg-white w-full">
            <SelectValue placeholder="Filter by criticality" />
          </SelectTrigger>
          <SelectContent>
            {criticalityOptions.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SelectClose isVisible={Boolean(criticality)} onClose={() => onCriticalityChange(null)} />
      </div>
    </div>
  );
};

export default TasksFilterBar;
